import { useState } from "react";
import "../marketing.css";
import { CoprocessorLogo } from "../components/CoprocessorLogo";
import { ContactModal } from "../components/ContactModal";
import { Footer } from "../components/Footer";
import { FlowHero } from "../components/marketing/FlowHero";
import { SyncGraphic } from "../components/marketing/SyncGraphic";
import { PipelineGraphic } from "../components/marketing/PipelineGraphic";
import { CoprocessorGraphic, ExportGraphic, ScriptsGraphic } from "../components/marketing/GapGraphics";
import { BenefitIcon } from "../components/marketing/BenefitIcons";
import { SceneVideo } from "../components/marketing/SceneVideo";
import { useRelease } from "../hooks/use-release";
import { PRODUCT_PATH, onLinkClick } from "../lib/router";

const BENCHMARK_URL = "/assets/coprocessor-benchmark.pdf";

const CHECK = (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path d="M2 6.5l2.6 2.5L10 3.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function ProductLink({ className, hash, children }: { className: string; hash?: string; children: React.ReactNode }) {
  const path = hash ? `${PRODUCT_PATH}#${hash}` : PRODUCT_PATH;
  return (
    <a href={path} className={className} onClick={onLinkClick(path)}>
      {children}
    </a>
  );
}

export function MarketingPage() {
  const [contactOpen, setContactOpen] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);
  const release = useRelease();

  return (
    <div className="mk">
      <nav className="mk-nav">
        <a href="/" className="mk-nav-brand" onClick={onLinkClick("/")}>
          <CoprocessorLogo width={30} height={30} /> Coprocessor
        </a>
        <div className="mk-nav-links">
          <a href="#why">Why</a>
          <a href="#how">How it works</a>
          <a href="#product">Product</a>
          <a href="#market">Integrators</a>
          <a href="#pricing">Pricing</a>
          <button onClick={() => setContactOpen(true)}>Contact</button>
          <ProductLink className="mk-nav-cta">Download &amp; docs →</ProductLink>
        </div>
      </nav>

      {/* ── Hero ───────────────────────────────────────────── */}
      <header className="mk-dark mk-hero" id="top">
        <div className="mk-video-bg"><SceneVideo /></div>
        <div className="mk-hero-glow" />
        <div className="mk-hero-grid" />
        <div className="mk-inner">
          <div className="mk-hero-copy">
            <div className="eyebrow-xl mk-rise">Coprocessor for Ignition integrators</div>
            <h1 className="mk-rise" style={{ marginTop: 22, ["--delay" as string]: ".08s" }}>
              <span className="nowrap">Turn your Ignition tags</span>
              <br />
              <span style={{ color: "var(--mk-cyan)" }}><span className="nowrap">into operational decisions</span><br /><span className="nowrap">in real time.</span></span>
            </h1>
            <p className="lede-xl muted mk-rise" style={{ marginTop: 28, maxWidth: "none", ["--delay" as string]: ".16s" }}>
              Leverage the simplicity of SQL to bring advanced analytics to more machines,
              more signals, and more demanding workloads, with a native C++ engine that
              demonstrated 6× Jython's processing capacity and analytics across 110,000 input
              tags in our{" "}
              <a className="mk-bench-link" href={BENCHMARK_URL} target="_blank" rel="noreferrer">benchmarks</a>.
            </p>
            <div className="mk-hero-actions mk-rise" style={{ ["--delay" as string]: ".24s" }}>
              <ProductLink className="mk-btn mk-btn-amber">Try it free on your gateway →</ProductLink>
              <button className="mk-btn mk-btn-outline" onClick={() => setDemoOpen(true)}>Request a demo</button>
            </div>
          </div>

          <div className="mk-hero-visual mk-rise" style={{ ["--delay" as string]: ".35s" }}>
            <FlowHero />
          </div>

          <div className="mk-hero-kpis">
            <div className="mk-hero-kpi"><b>Streaming SQL</b><span>Queries over tags. Rolling computation and time-aware semantics, powered by C++.</span></div>
            <div className="mk-hero-kpi"><b>6x faster</b><span>510 processors updated every second versus 85 Jython tag scripts at most.</span></div>
            <div className="mk-hero-kpi"><b>Native to Ignition</b><span>Match input tags with a pattern. Results land back in the same tag tree.</span></div>
          </div>
        </div>
      </header>


      {/* ── Why: the computation gap ───────────────────────── */}
      <section className="mk-dark mk-section" id="why" style={{ background: "var(--mk-night-2)", borderTop: "1px solid var(--mk-line-d)" }}>
        <div className="mk-inner">
          <div className="eyebrow-xl">01 · The computation gap</div>
          <h2 style={{ marginTop: 18 }} className="mk-h2-gap">
            <span className="nowrap">Tag analysis still means</span>
            <br />
            <span className="nowrap" style={{ color: "var(--mk-cyan)" }}>scripting or a cloud bill.</span>
          </h2>
          <p className="lede-xl muted" style={{ marginTop: 22, maxWidth: "none" }}>
            Pressure, current, vibration and energy arrive continuously. Turning related changes
            across several tags into one operational decision still means choosing between
            two options integrators know too well. Coprocessor is our answer, so you no longer have to
            choose between fragile scripts and a cloud bill.
          </p>

          <div className="mk-cards">
            <div className="mk-card">
              <ScriptsGraphic />
              <h3>Tag scripting</h3>
              <p className="muted">Scattered across tag change events and PLC code. Not time-aware, no rolling computation, impossible to backtest, and nothing to reuse on the next project.</p>
              <div className="verdict" style={{ color: "var(--mk-red)" }}>fragile at scale</div>
            </div>
            <div className="mk-card">
              <ExportGraphic />
              <h3>Cloud analytics</h3>
              <p className="muted">History leaves the plant for a cloud subscription tool. The answer is right, but it arrives late or it does not arrive, and every gateway you connect adds to the bill.</p>
              <div className="verdict" style={{ color: "var(--mk-red)" }}>late · and a line item</div>
            </div>
            <div className="mk-card hot">
              <CoprocessorGraphic />
              <h3>Coprocessor</h3>
              <p className="muted">Write SQL statements and reuse them across sites. Backtest them on recorded history, run them inside the gateway to drive operational decisions in real time.</p>
              <div className="verdict" style={{ color: "var(--mk-cyan)" }}>live · reusable · local</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Synchronization ────────────────────────────────── */}
      <section className="mk-light mk-section" id="sync">
        <div className="mk-inner">
          <div className="mk-stack">
            <div className="mk-row-copy">
              <div className="eyebrow-xl">02 · Synchronization</div>
              <h2 style={{ marginTop: 18 }} className="mk-h2-sync"><span className="nowrap">Different clocks.</span><br /><span className="nowrap">Different rates.</span><br /><span className="nowrap" style={{ color: "var(--accent-hover)" }}>One timeline.</span><br /><span className="nowrap" style={{ color: "var(--accent-hover)" }}>6x more tags.</span></h2>
              <p className="lede-xl muted">
                A PLC scans at 50 ms, a pump reports every second, a vibration sensor bursts.
                Coprocessor aligns them on one sample grid before any math happens, so a window over
                three signals means the same thirty seconds for all three.
              </p>
            </div>
            <div className="mk-sync-row">
              <ul className="mk-list" style={{ marginTop: 0 }}>
                <li><i>{CHECK}</i><span>Time-aligned joins across tags with different update rates</span></li>
                <li><i>{CHECK}</i><span>Rolling computation that holds state between samples</span></li>
                <li><i>{CHECK}</i><span>One processor instance per single tag pattern matched</span></li>
                <li><i>{CHECK}</i><span>6x more tags processed than existing technology</span></li>
              </ul>
              <div className="mk-frame mk-frame-flat">
                <SyncGraphic />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── How it works ───────────────────────────────────── */}
      <section className="mk-dark mk-section" id="how">
        <div className="mk-inner">
          <div className="eyebrow-xl">03 · How it works</div>
          <h2 style={{ marginTop: 18, maxWidth: 940 }}>From a tag pattern to a running processor, <span style={{ color: "var(--mk-cyan)" }}>in SQL.</span></h2>
          <p className="lede-xl muted" style={{ marginTop: 22, maxWidth: "none" }}>
            Define streams from tag patterns and write SQL statements. Build and validate them
            against historical plant data, then deploy the same streaming SQL model inside
            Ignition. The result is a processor that runs inside the gateway and shows up next to
            everything else in the Operations dashboard.
          </p>
          <div className="mk-frame" style={{ marginTop: 56 }}>
            <PipelineGraphic />
          </div>
          <div className="mk-cards" style={{ marginTop: 28 }}>
            {([
              ["01", "Author", "SQL is compiled in the browser via WASM. Errors and the output catalog appear inline before you commit to running anything on the gateway.", BenefitIcon.Pen],
              ["02", "Test", "Match input tags with a pattern and run the processor(s) over real tags or historical data in the browser. Nothing is written to the gateway yet.", BenefitIcon.Flask],
              ["03", "Deploy", "The processor runs natively inside the gateway and writes its outputs back as tags in real time. No outbound telemetry.", BenefitIcon.Rocket],
            ] as const).map(([n, t, b, Icon]) => (
              <div className="mk-card" key={n}>
                <div className="eyebrow-xl" style={{ color: "var(--mk-amber)" }}>Step {n}</div>
                <div className="mk-benefit-head">
                  <i className="mk-benefit-icon mk-benefit-icon-dark"><Icon /></i>
                  <h3>{t}</h3>
                </div>
                <p className="muted">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Product ────────────────────────────────────────── */}
      <section className="mk-light mk-paper mk-section" id="product">
        <div className="mk-inner">
          <div className="eyebrow-xl">04 · Working today</div>
          <h2 style={{ marginTop: 18 }} className="mk-h2-prod"><span className="nowrap">Current module,</span><br /><span className="nowrap">running <span style={{ color: "var(--accent-hover)" }}>inside Ignition.</span></span></h2>
          <p className="lede-xl muted" style={{ marginTop: 22, maxWidth: "none" }}>
            Coprocessor adds three tabs to the Ignition gateway. Engineering is where you write SQL
            statements, compile them, and test them against recorded history or live tags. Operations is where
            processors run. Check status and throughput, and open a live explorer for every input
            and output signal. License is where you activate the Coprocessor module.
          </p>
          <div className="mk-shot">
            <div className="mk-shot-bar"><i /><i /><i /></div>
            <img src="/assets/coprocessor-engineering.jpeg" alt="Coprocessor Engineering tab inside the Ignition gateway web interface, with a SQL script defining a stream from a tag pattern and a materialized view, compiled successfully." />
          </div>
          <div className="mk-shot" style={{ marginTop: 28 }}>
            <div className="mk-shot-bar"><i /><i /><i /></div>
            <img src="/assets/coprocessor-operations.jpeg" alt="Coprocessor Operations tab inside the Ignition gateway web interface, showing a running processor and its live input and computed output signals." />
          </div>
          <div className="mk-annots">
            <div className="mk-annot"><b>1</b><div><h3 style={{ fontSize: 20 }}>Write SQL</h3><p>Streams and views in an editor that validates as you type.</p></div></div>
            <div className="mk-annot"><b>2</b><div><h3 style={{ fontSize: 20 }}>Compile the processor</h3><p>SQL becomes an operator graph the native engine executes.</p></div></div>
            <div className="mk-annot"><b>3</b><div><h3 style={{ fontSize: 20 }}>Inspect live output</h3><p>Watch inputs and derived values update in place, next to your other processors.</p></div></div>
          </div>
          <div className="mk-hero-actions" style={{ marginTop: 44 }}>
            <ProductLink className="mk-btn mk-btn-dark">Download &amp; docs →</ProductLink>
            <ProductLink className="mk-btn mk-btn-outline" hash="requirements">Requirements &amp; install</ProductLink>
          </div>
        </div>
      </section>

      {/* ── Market ─────────────────────────────────────────── */}
      <section className="mk-light mk-section" id="market">
        <div className="mk-inner">
          <div className="eyebrow-xl">05 · For Ignition integrators</div>
          <h2 style={{ marginTop: 18 }} className="mk-h2-market"><span className="nowrap">Built for the people</span><br /><span className="nowrap" style={{ color: "var(--accent-hover)" }}>who deliver Ignition.</span></h2>
          <p className="lede-xl muted" style={{ marginTop: 22, maxWidth: "none" }}>
            If you deliver Ignition projects, you know the calculations that keep escaping into
            scripts and spreadsheets. Coprocessor gives you the power to harness time-aware
            computational complexity for the price of writing SQL statements, with no cloud line item to justify.
          </p>
          <div className="mk-benefits">
            <div className="mk-benefit">
              <div className="mk-benefit-head">
                <i className="mk-benefit-icon"><BenefitIcon.Code /></i>
                <b>Fewer code hours</b>
              </div>
              <span>Replace per-project scripts with SQL statements your team already knows how to write and review.</span>
            </div>
            <div className="mk-benefit">
              <div className="mk-benefit-head">
                <i className="mk-benefit-icon"><BenefitIcon.Clock /></i>
                <b>Time-aware by design</b>
              </div>
              <span>Rolling computation and time-aligned streams are built into the SQL plan. The complexity lives in the engine, not in your code.</span>
            </div>
            <div className="mk-benefit">
              <div className="mk-benefit-head">
                <i className="mk-benefit-icon"><BenefitIcon.CloudOff /></i>
                <b>No cloud line item</b>
              </div>
              <span>Runs on the gateway the customer already owns. Nothing to subscribe to, nothing leaving the plant.</span>
            </div>
            <div className="mk-benefit">
              <div className="mk-benefit-head">
                <i className="mk-benefit-icon"><BenefitIcon.Shield /></i>
                <b>Proven before go-live</b>
              </div>
              <span>Backtest on historical data and on live tags in the browser before a single tag is written.</span>
            </div>
            <div className="mk-benefit">
              <div className="mk-benefit-head">
                <i className="mk-benefit-icon"><BenefitIcon.Layers /></i>
                <b>Scales to the whole plant</b>
              </div>
              <span>110,000 inputs on one gateway, each updating every five seconds, 22,000 bursts per second. More machines and more signals without a second server. <a className="mk-bench-link" href={BENCHMARK_URL} target="_blank" rel="noreferrer">Read the benchmark</a></span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Pricing ────────────────────────────────────────── */}
      <section className="mk-dark mk-section" id="pricing">
        <div className="mk-inner">
          <div className="eyebrow-xl">06 · Pricing</div>
          <h2 style={{ marginTop: 18 }} className="mk-h2-price"><span className="nowrap">A gateway license with a</span><br /><span className="nowrap" style={{ color: "var(--mk-cyan)" }}>clear support path.</span></h2>
          <div className="mk-price-grid">
            <div className="mk-price">
              <div className="eyebrow-xl" style={{ color: "var(--mk-amber)" }}>Perpetual license</div>
              <div className="amount">$6,000<small>per gateway</small></div>
              <ul>
                <li>First year of updates and support included</li>
                <li>Annual maintenance renewal at 20%</li>
                <li>Offline license verification, no outbound telemetry</li>
                <li>Continuous operations, processors do not stop</li>
              </ul>
              <div className="mk-hero-actions" style={{ marginTop: 32 }}>
                <ProductLink className="mk-btn mk-btn-amber" hash="pricing">Buy or compare plans →</ProductLink>
              </div>
            </div>
            <div className="mk-price trial">
              <div className="eyebrow-xl">Free trial</div>
              <div className="amount">$0<small>to evaluate</small></div>
              <ul>
                <li>Coprocessor on your own gateway</li>
                <li>Full capacity, no computing limits</li>
                <li>Real tags, real signals, your SQL</li>
                <li>Processors stop every 2 hours</li>
              </ul>
              <div className="mk-hero-actions" style={{ marginTop: 32 }}>
                <ProductLink className="mk-btn mk-btn-outline" hash="final-cta">Download the trial</ProductLink>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Final CTA ──────────────────────────────────────── */}
      <section className="mk-dark mk-final">
        <div className="mk-inner" style={{ position: "relative" }}>
          <div className="eyebrow-xl">Ready when your tags are</div>
          <h2 style={{ marginTop: 22 }}>Build up an <span style={{ color: "var(--mk-cyan)" }}>analytics powerhouse</span> out of your Ignition gateway.</h2>
          <div className="mk-hero-actions">
            <ProductLink className="mk-btn mk-btn-amber">Go to download &amp; docs →</ProductLink>
            <button className="mk-btn mk-btn-outline" onClick={() => setDemoOpen(true)}>Request a demo</button>
          </div>
          <div className="mk-final-note">Ignition 8.3.0+ · Linux, Windows and macOS gateways · installs like any other module</div>
        </div>
      </section>

      <Footer release={release} onContactClick={() => setContactOpen(true)} />
      <ContactModal open={contactOpen} onClose={() => setContactOpen(false)} />
      <ContactModal
        open={demoOpen}
        onClose={() => setDemoOpen(false)}
        title="Request a demo"
        subtitle="Tell us about your gateway and the tags you'd like to see it run on. We'll set up a walkthrough."
        initialMessage="I'd like a demo of Coprocessor."
      />
    </div>
  );
}
